import { Field as FormField, FieldGroup, Label } from '../../catalyst/fieldset'
import { Input } from '../../catalyst/input'
import { dropNulls } from '../../lib/api'
import type { FertilizerLog } from '../../lib/types'
import { SameAsLast } from '../chips'
import { amountProblem, optNum, optText, seasonYearOf, str, type FlowDef } from '../flow'
import { AmountInput, lastFieldLog } from './shared'
import { rememberWho, WhoField } from '../who'

export const fertilizerFlow: FlowDef = {
  kind: 'fertilizer',
  checkForm: 'h2Fertilizer',
  label: 'Fertilizer',
  needsField: true,
  initial: ({ today, fieldId }) => ({ fieldId, date: today, rateUnit: 'kg/ha' }),
  fetchLast: (orgId, farmId, fieldId) => lastFieldLog<FertilizerLog>('fertilizer-logs', orgId, farmId, fieldId),
  steps: [
    {
      id: 'what',
      title: 'What did you put on?',
      valid: (v) => str(v.blend).trim() !== '' && !amountProblem(v.rateAmount),
      render: (ctx) => {
        const { values, patch, last } = ctx
        const l = last as FertilizerLog | null
        return (
          <FieldGroup>
            {l && (
              <SameAsLast
                label={[l.blend, l.rateAmount != null && `${l.rateAmount} ${l.rateUnit ?? ''}`].filter(Boolean).join(', ')}
                onClick={() =>
                  patch({ blend: l.blend, rateAmount: l.rateAmount == null ? '' : String(l.rateAmount), rateUnit: l.rateUnit ?? 'kg/ha', applicator: l.applicatorName ?? '' })
                }
              />
            )}
            <FormField>
              <Label>Fertilizer or blend</Label>
              <Input value={str(values.blend)} onChange={(e) => patch({ blend: e.target.value })} placeholder="e.g. 12-12-12" autoComplete="off" />
            </FormField>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Rate</Label>
                <AmountInput value={values.rateAmount} onChange={(r) => patch({ rateAmount: r })} />
              </FormField>
              <FormField className="w-28">
                <Label>Per</Label>
                <Input value={str(values.rateUnit)} onChange={(e) => patch({ rateUnit: e.target.value })} />
              </FormField>
            </div>
            <FormField>
              <Label>Lot number (optional)</Label>
              <Input value={str(values.lotNumber)} onChange={(e) => patch({ lotNumber: e.target.value })} autoComplete="off" />
            </FormField>
            <WhoField ctx={ctx} />
          </FieldGroup>
        )
      },
    },
  ],
  review: ({ values, field }) => ({
    title: `Fertilizer${field ? ` · ${field.name}` : ''}`,
    lines: [
      { label: 'Date', value: str(values.date) },
      { label: 'Fertilizer', value: str(values.blend) },
      ...(optNum(values.rateAmount) != null ? [{ label: 'Rate', value: `${str(values.rateAmount)} ${str(values.rateUnit)}` }] : []),
      ...(optText(values.lotNumber) ? [{ label: 'Lot number', value: str(values.lotNumber) }] : []),
      ...(optText(values.applicator) ? [{ label: 'Applied by', value: str(values.applicator) }] : []),
    ],
  }),
  entry: (ctx) => {
    const { values } = ctx
    const date = str(values.date)
    const rate = optNum(values.rateAmount)
    return dropNulls({
      fieldId: str(values.fieldId),
      seasonYear: seasonYearOf(date),
      appliedOn: date,
      blend: str(values.blend).trim(),
      rateAmount: rate ?? null,
      rateUnit: rate == null ? null : (optText(values.rateUnit) ?? null),
      lotNumber: optText(values.lotNumber) ?? null,
      applicatorName: optText(values.applicator) ?? null,
    })
  },
  badge: { framework: 'CanadaGAP', formCode: 'H2', formName: 'Agronomic Inputs (Fertilizers)' },
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/fertilizer-logs`,
  onRecorded: rememberWho(),
}
